import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NextArrow from "@tSurya/Icons/NextArrow";
import PlayIcon from "@tSurya/Icons/PlayIcon";
import PreviousArrow from "@tSurya/Icons/PreviousArrow";
import ChevronLeft from "@tSurya/MobiusOverrides/Icons/ChevronLeft";
import ChevronRight from "@tSurya/MobiusOverrides/Icons/ChevronRight";
import { COLORS } from "@tSurya/theme/design";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    selectedIndex?: number;
    onSelectImage?: (index: number) => void;
    extendedStyles?: ProductImageCarouselStyles;
    isModalOpen?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    selectedIndexImageStore: state.pages.productDetails.selectedImageIndex,
});

type Props = OwnProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductImageCarouselStyles {
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowGridItemModal?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    playIcon?: InjectableCss;
    image?: ImgProps;
    selectedImage?: ImgProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowGridItemModal?: GridItemProps;
    nextArrowButton?: ButtonProps;
}

const getImageCss = (borderColor: string) => css`
    width: 100%;
    height: 100%;
    cursor: pointer;
    justify-content: flex-start;

    img {
        aspect-ratio: 85/85.8;
        object-fit: contain;
        max-width: 85px;
        /* mix-blend-mode: multiply; */

        background-color: ${COLORS.floralWhite};
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    aspect-ratio: 50/50;
                    max-width: 50px !important;
                `,
                css`
                    aspect-ratio: 50/50;
                    max-width: 50px !important;
                `,
                css`
                    aspect-ratio: 50/50;
                    max-width: 85px !important;
                `,
                null,
                null,
            ])}
    }
`;

export const productImageCarouselStyles: ProductImageCarouselStyles = {
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
            width: 100%;
            > div[class^="GridOffset"] {
                align-items: center;
                justify-content: center;
                //   max-height: 700px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            > div[class^="GridOffset"] {
                                margin-top: 30px;
                                display: flex;
                                flex-direction: unset;
                                width: 100%;
                                flex-wrap: nowrap;
                            }
                        `,
                        css`
                            > div[class^="GridOffset"] {
                                margin-top: 30px;
                                display: flex;
                                flex-direction: unset;
                                width: 100%;
                                flex-wrap: nowrap;
                            }
                        `,
                        css`
                            > div[class^="GridOffset"] {
                                margin-top: 30px;
                                display: flex;
                                flex-direction: unset;
                                width: 100%;
                                flex-wrap: nowrap;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    prevArrowGridItem: {
        css: css`
            transform: rotate(270deg);
            max-width: none;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            transform: rotate(180deg);
                            justify-content: flex-end;
                            align-items: center;
                        `,
                        css`
                            transform: rotate(180deg);
                            justify-content: flex-end;
                            align-items: center;
                        `,
                        css`
                            transform: rotate(180deg);
                            justify-content: center;
                            align-items: center;
                        `,
                        css``,
                        css``,
                    ],
                    "minMax",
                )}
        `,
    },
    carouselGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            //  max-height: 625px;
            justify-content: center;
            overflow: hidden;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: auto;
                    `,
                    css`
                        height: auto;
                    `,
                    css`
                        height: auto;
                    `,
                    null,
                    null,
                ])};
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            text-align: center;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            img {
                height: 85px;
                object-fit: contain;
                background-color: ${getColor("common.backgroundContrast")};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-bottom: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    image: {
        css: getImageCss("common.border"),
    },
    selectedImage: {
        css: css`
            ${getImageCss("text.main")};
            /* mix-blend-mode: multiply; */
            padding: 4px 5px;
            border: 1px solid ${getColor("common.border")};
        `,
    },
    nextArrowGridItem: {
        css: css`
            transform: rotate(90deg);
            max-width: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            transform: rotate(0deg);
                            justify-content: flex-start;
                            align-items: center;
                        `,
                        css`
                            transform: rotate(0deg);
                            justify-content: flex-start;
                            align-items: center;
                        `,
                        css`
                            transform: rotate(0deg);
                            justify-content: center;
                            align-items: center;
                        `,
                        null,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    playIcon: {
        css: css`
            display: flex;
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            align-items: center;
            justify-content: center;
        `,
    },
};

const ProductImageCarousel = ({
    productSettings,
    productContext,
    selectedIndex,
    onSelectImage,
    extendedStyles,
    isModalOpen,
    selectedIndexImageStore,
}: Props) => {
    productImageCarouselStyles.prevArrowGridItemModal = {
        css: css`
            transform: rotate(270deg);
            max-width: none;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            transform: rotate(180deg);
                            justify-content: flex-end;
                            align-items: center;
                        `,
                        css`
                            transform: rotate(180deg);
                            justify-content: flex-end;
                            align-items: center;
                        `,
                        css`
                            ${isModalOpen
                                ? `position: absolute;
                            top: -15px;`
                                : `transform: rotate(180deg);
                        justify-content: center;
                        align-items: center;`}
                        `,
                        css`
                            position: absolute;
                            top: -15px;
                        `,
                        css`
                            position: absolute;
                            top: -15px;
                        `,
                    ],
                    "minMax",
                )}
        `,
    };
    productImageCarouselStyles.prevArrowButton = {
        variant: "secondary",
        color: "text.main",
        css: css`
            transform: rotate(180deg);
            border: 0;
            padding: 0;
            :disabled {
                opacity: 0.3;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        :disabled {
                            opacity: 0;
                        }
                    `,
                    css`
                        :disabled {
                            opacity: 0;
                        }
                    `,
                    css`
                        ${isModalOpen
                            ? `:disabled {
                        opacity: 0.3;
                    }`
                            : `:disabled {
                        opacity: 0;
                    }`}
                    `,
                    null,
                    null,
                ])}
        `,
    };
    productImageCarouselStyles.carouselSlidesContainer = {
        css: css`
            display: flex;
            flex-direction: column;
            height: 625px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: row;
                            height: auto;
                        `,
                        css`
                            flex-direction: row;
                            height: auto;
                        `,
                        css`
                            ${isModalOpen
                                ? `flex-direction: column;
                        height: 625px;`
                                : `flex-direction: row;
                        height: auto;`}
                        `,
                        css`
                            flex-direction: column;
                            height: 625px;
                        `,
                        css`
                            flex-direction: column;
                            height: 625px;
                        `,
                    ],
                    "minMax",
                )};
        `,
    };
    productImageCarouselStyles.carouselSlide = {
        css: css`
            flex: 0 0 ${100 / 6}%;
            padding: 0;
            position: relative;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 0 0 ${100 / 6}%;
                        margin-right: 10px;
                    `,
                    css`
                        flex: 0 0 ${100 / 6}%;
                        margin-right: 10px;
                    `,
                    css`
                        ${isModalOpen
                            ? null
                            : `flex: 0 0 14%;
                    margin-right: 10px;`}
                    `,
                    null,
                    null,
                ])}
        `,
    };
    productImageCarouselStyles.nextArrowGridItemModal = {
        css: css`
            transform: rotate(90deg);
            max-width: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            transform: rotate(0deg);
                            justify-content: flex-start;
                            align-items: center;
                        `,
                        css`
                            transform: rotate(0deg);
                            justify-content: flex-start;
                            align-items: center;
                        `,
                        css`
                            ${isModalOpen
                                ? `position: absolute;
                       bottom: -15px;`
                                : `transform: rotate(0deg);
                        justify-content: center;
                        align-items: center;`}
                        `,
                        css`
                            position: absolute;
                            bottom: -15px;
                        `,
                        css`
                            position: absolute;
                            bottom: -15px;
                        `,
                    ],
                    "minMax",
                )}
        `,
    };
    productImageCarouselStyles.nextArrowButton = {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            :disabled {
                opacity: 0.3;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        :disabled {
                            opacity: 0;
                        }
                    `,
                    css`
                        :disabled {
                            opacity: 0;
                        }
                    `,
                    css`
                        ${isModalOpen
                            ? `:disabled {
                        opacity: 0.3;
                    }`
                            : `:disabled {
                        opacity: 0;
                    }`}
                    `,
                    null,
                    null,
                ])}
        `,
    };

    const [styles] = useState(() => mergeToNew(productImageCarouselStyles, extendedStyles));
    const { product } = productContext;

    const [selectedImageIndex, setSelectedImageIndex] = useState(selectedIndex);
    useEffect(() => {
        setSelectedImageIndex(selectedIndex);
    }, [selectedIndex, selectedIndexImageStore]);

    const imageClickHandler = (index: number) => {
        setSelectedImageIndex(index);
        onSelectImage?.(index);
    };

    // const videoRegex = /^[0-9]{1,2}\.(?:mp4|mpg|avi)$/;

    // const viemoRegex =
    /(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_\-]+)?/i;
    const youtubeRegex = /^[a-zA-Z0-9_-]{11}$/;

    const [emblaRef, embla] = useEmblaCarousel();
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);
    }, [embla]);
    const handleResize=()=>{
        if (!embla || !product.images) {
            return;
        }
        if (window.innerWidth > 1200) {
            if (document.getElementById("horizontal-carousel-show")) {
                embla.reInit({
                    align: product.images.length > 1 ? "start" : "center",
                    slidesToScroll: 1,
                    axis: "x",
                    draggable: true,
                    containScroll: "trimSnaps",
                });
                setCanScroll();
            } else {
                embla.reInit({
                    align: product.images.length > 1 ? "start" : "center",
                    slidesToScroll: 1,
                    axis: "y",
                    draggable: true,
                    containScroll: "trimSnaps",
                });
                setCanScroll();
            }
        } else {
            embla.reInit({
                align: product.images.length > 1 ? "start" : "center",
                draggable: true,
                slidesToScroll: 1,
                axis: window.innerWidth > 991 ? "y" : isModalOpen ? "y" : "x",
                containScroll: "trimSnaps",
            });
            setCanScroll();
        }
      
    }

    useEffect(() => {
        handleResize(); // Call initially to set the state based on the current window size
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [embla, product]);

    const getProductImageThumbPath = (productImage: ImageModel) => {
        
        const mediumImagePath = productImage?.mediumImagePath;
        const smallImagePath = productImage?.smallImagePath;
    
        if (mediumImagePath && smallImagePath && youtubeRegex.test(mediumImagePath) && youtubeRegex.test(smallImagePath)) {
          const videoId = mediumImagePath;
          return `https://img.youtube.com/vi/${videoId}/0.jpg`;
        }    
        if (productImage.imageType === "Static") {
            return productImage.smallImagePath || productImage.mediumImagePath;
        }
        if (productImage.imageType === "360" && productSettings.imageProvider === "SIRV") {
            return productImage.mediumImagePath + (productImage.mediumImagePath.indexOf("?") > 0 ? "&thumb" : "?thumb");
        }
        return "";
    };

    if (!product || !product.images || product.images?.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.mainContainer} key={product.id}>
            {isModalOpen ? (
                <GridItem {...styles.prevArrowGridItemModal}>
                    {product.images.length > 6 ? (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            role="button"
                            aria-label="Previous Arrow"
                        >
                            <ButtonIcon src={PreviousArrow} />
                        </Button>
                    ) : null}
                </GridItem>
            ) : (
                <GridItem {...styles.prevArrowGridItem}>
                    {product.images.length > 6 ? (
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            role="button"
                            aria-label="Previous Arrow"
                        >
                            <ButtonIcon src={PreviousArrow} />
                        </Button>
                    ) : null}
                </GridItem>
            )}

            <GridItem {...styles.carouselGridItem} style={{ padding: isModalOpen ? "24px 0" : 0 }}>
                <StyledWrapper {...styles.carouselWrapper}>
                    <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                        <StyledWrapper {...styles.carouselSlidesContainer} className="slideContainer">
                            {product.images.map((productImage, index) => (
                                <StyledWrapper {...styles.carouselSlide} key={productImage.id} className="embla-slides">
                                    <StyledWrapper {...styles.carouselSlideInner}>
                                        <Clickable
                                            onClick={() => imageClickHandler(index)}
                                            css={css`
                                                width: 100%;
                                                max-width: 85px;
                                                /* background-color: ${getColor("common.backgroundContrast")}; */
                                                img {
                                                    /* mix-blend-mode: multiply; */
                                                }
                                            `}
                                        >
                                            <Img
                                                loading="lazy"
                                                {...(index === selectedImageIndex
                                                    ? styles.selectedImage
                                                    : styles.image)}
                                                src={getProductImageThumbPath(productImage)}
                                                altText={productImage.imageAltText}
                                            />
                                            {youtubeRegex.test(productImage.mediumImagePath) ? (
                                                <StyledWrapper {...styles.playIcon}>
                                                    <PlayIcon />
                                                </StyledWrapper>
                                            ) : null}
                                        </Clickable>
                                    </StyledWrapper>
                                </StyledWrapper>
                            ))}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            {isModalOpen ? (
                <GridItem {...styles.nextArrowGridItemModal}>
                    {product.images.length > 6 ? (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            role="button"
                            aria-label="Next Arrow"
                        >
                            <ButtonIcon src={NextArrow} />
                        </Button>
                    ) : null}
                </GridItem>
            ) : (
                <GridItem {...styles.nextArrowGridItem}>
                    {product.images.length > 6 ? (
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            role="button"
                            aria-label="Next Arrow"
                        >
                            <ButtonIcon src={NextArrow} />
                        </Button>
                    ) : null}
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(withProductContext(ProductImageCarousel));
